import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import type { Trace, TraceEvent, TraceStep } from '@shared/trace';
import {
  api,
  ApiError,
  subscribeToTraces,
  type BootstrapPayload,
  type PendingApproval,
  type ScenarioSummary,
} from '../api';

/**
 * The console's single source of truth.
 *
 * Traces arrive as a stream of small updates rather than finished objects, so
 * the store's job is to keep an ordered, always-current view of them while the
 * server is still working. Everything the UI renders is derived from here —
 * there is no second copy of a trace living inside a component.
 */

interface State {
  /** `locked` means the hosted deployment wants the shared password first. */
  status: 'loading' | 'locked' | 'ready' | 'error';
  error?: string;
  bootstrap?: BootstrapPayload;
  /** Newest first. */
  traces: Trace[];
  /** The trace the Action Theater is currently showing. */
  focusedTraceId?: string;
  approvals: PendingApproval[];
  /** Set while a scenario is mid-flight, to narrate what is coming. */
  activeScenario?: { id: string; title: string; talkTrack: string[]; step: number; total: number };
  streamConnected: boolean;
}

type Action =
  | { type: 'bootstrap/loaded'; payload: BootstrapPayload }
  | { type: 'bootstrap/failed'; error: string }
  | { type: 'bootstrap/locked' }
  | { type: 'trace/event'; event: TraceEvent }
  | { type: 'trace/focus'; traceId?: string }
  | { type: 'trace/hydrate'; traces: Trace[] }
  | { type: 'approvals/set'; approvals: PendingApproval[] }
  | { type: 'scenario/start'; id: string; title: string; talkTrack: string[]; total: number }
  | { type: 'scenario/advance' }
  | { type: 'scenario/end' }
  | { type: 'stream/status'; connected: boolean }
  | { type: 'operator/set'; operatorId: string };

const MAX_TRACES = 40;

function upsertStep(trace: Trace, step: TraceStep): Trace {
  const steps = trace.steps.slice();
  const index = steps.findIndex(candidate => candidate.id === step.id);
  if (index === -1) steps.push(step);
  else steps[index] = step;
  return { ...trace, steps };
}

function reduceTraceEvent(traces: Trace[], event: TraceEvent): Trace[] {
  if (event.type === 'trace.started') {
    const next = [event.trace, ...traces.filter(trace => trace.id !== event.trace.id)];
    return next.slice(0, MAX_TRACES);
  }

  // `hello` and `fleet.tick` are stream-level, not about any one trace.
  if (!('traceId' in event)) return traces;

  return traces.map(trace => {
    if (trace.id !== event.traceId) return trace;

    switch (event.type) {
      case 'trace.step':
        return upsertStep(trace, event.step);

      case 'trace.line': {
        // Terminal output belongs to the execution step's detail, appended in
        // arrival order so the operator watches it scroll.
        const steps = trace.steps.map(step => {
          if (step.id !== 'execution' || step.detail?.kind !== 'execution') return step;
          return { ...step, detail: { ...step.detail, lines: [...step.detail.lines, event.line] } };
        });
        return { ...trace, steps };
      }

      case 'trace.mirror':
        return { ...trace, mirror: event.mirror };

      case 'trace.completed':
        return { ...trace, outcome: event.outcome, endedAt: event.endedAt, narration: event.narration };

      default:
        return trace;
    }
  });
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'bootstrap/loaded':
      return { ...state, status: 'ready', bootstrap: action.payload };

    case 'bootstrap/failed':
      return { ...state, status: 'error', error: action.error };

    case 'bootstrap/locked':
      return { ...state, status: 'locked', error: undefined };

    case 'trace/hydrate':
      return { ...state, traces: action.traces.slice(0, MAX_TRACES) };

    case 'trace/event': {
      const traces = reduceTraceEvent(state.traces, action.event);
      // A newly started trace takes focus automatically. During a demo the
      // presenter should never have to click to see the thing they just did.
      const focusedTraceId =
        action.event.type === 'trace.started' ? action.event.trace.id : state.focusedTraceId;
      return { ...state, traces, focusedTraceId };
    }

    case 'trace/focus':
      return { ...state, focusedTraceId: action.traceId };

    case 'approvals/set':
      return { ...state, approvals: action.approvals };

    case 'scenario/start':
      return {
        ...state,
        activeScenario: { id: action.id, title: action.title, talkTrack: action.talkTrack, step: 0, total: action.total },
      };

    case 'scenario/advance':
      return state.activeScenario
        ? { ...state, activeScenario: { ...state.activeScenario, step: state.activeScenario.step + 1 } }
        : state;

    case 'scenario/end':
      return { ...state, activeScenario: undefined };

    case 'stream/status':
      return { ...state, streamConnected: action.connected };

    case 'operator/set':
      return state.bootstrap
        ? { ...state, bootstrap: { ...state.bootstrap, session: { ...state.bootstrap.session, operatorId: action.operatorId } } }
        : state;

    default:
      return state;
  }
}

const initialState: State = {
  status: 'loading',
  traces: [],
  approvals: [],
  streamConnected: false,
};

interface StoreValue extends State {
  focusedTrace?: Trace;
  scenarios: ScenarioSummary[];
  focusTrace: (traceId?: string) => void;
  /** Submit the shared password; resolves once the console is unlocked. */
  signIn: (password: string) => Promise<void>;
  setOperator: (operatorId: string) => Promise<void>;
  runScenario: (scenarioId: string) => Promise<void>;
  refreshApprovals: () => Promise<void>;
  resolveApproval: (traceId: string, decision: 'approved' | 'rejected', approverId: string) => Promise<void>;
}

const StoreContext = createContext<StoreValue | undefined>(undefined);

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const approvalsTimer = useRef<number>();

  const load = useCallback(async () => {
    try {
      dispatch({ type: 'bootstrap/loaded', payload: await api.bootstrap() });
    } catch (error) {
      // 401 is not a failure, it is the hosted deployment asking for its
      // password. Anything else genuinely went wrong.
      if (error instanceof ApiError && error.status === 401) dispatch({ type: 'bootstrap/locked' });
      else dispatch({ type: 'bootstrap/failed', error: (error as Error).message });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const signIn = useCallback(
    async (password: string) => {
      await api.signIn(password);
      await load();
    },
    [load],
  );

  // The stream is opened only once the console is unlocked: an SSE connection
  // that 401s just reconnects forever behind the sign-in screen.
  useEffect(() => {
    if (state.status !== 'ready') return;

    const unsubscribe = subscribeToTraces(event => {
      dispatch({ type: 'trace/event', event });
      if (event.type === 'trace.completed' || event.type === 'approval.updated') {
        void api.approvals().then(({ approvals }) => dispatch({ type: 'approvals/set', approvals }));
      }
    });
    dispatch({ type: 'stream/status', connected: true });
    return () => {
      unsubscribe();
      dispatch({ type: 'stream/status', connected: false });
    };
  }, [state.status]);

  // Approvals are polled as well as streamed, because one can be resolved in the
  // AgentGuard console — an event this browser never sees.
  useEffect(() => {
    const poll = () => {
      void api.approvals().then(({ approvals }) => dispatch({ type: 'approvals/set', approvals }));
    };
    poll();
    approvalsTimer.current = window.setInterval(poll, 5000);
    return () => window.clearInterval(approvalsTimer.current);
  }, []);

  const focusTrace = useCallback((traceId?: string) => dispatch({ type: 'trace/focus', traceId }), []);

  const setOperator = useCallback(async (operatorId: string) => {
    await api.setOperator(operatorId);
    dispatch({ type: 'operator/set', operatorId });
  }, []);

  const runScenario = useCallback(
    async (scenarioId: string) => {
      const scenario = state.bootstrap?.scenarios.find(candidate => candidate.id === scenarioId);
      dispatch({
        type: 'scenario/start',
        id: scenarioId,
        title: scenario?.title ?? scenarioId,
        talkTrack: scenario?.talkTrack ?? [],
        total: 1,
      });
      try {
        await api.runScenario(scenarioId);
      } finally {
        dispatch({ type: 'scenario/end' });
      }
    },
    [state.bootstrap],
  );

  const refreshApprovals = useCallback(async () => {
    const { approvals } = await api.approvals();
    dispatch({ type: 'approvals/set', approvals });
  }, []);

  const resolveApproval = useCallback(
    async (traceId: string, decision: 'approved' | 'rejected', approverId: string) => {
      await api.resolveApproval(traceId, decision, approverId);
      await refreshApprovals();
    },
    [refreshApprovals],
  );

  const value = useMemo<StoreValue>(
    () => ({
      ...state,
      focusedTrace: state.traces.find(trace => trace.id === state.focusedTraceId),
      scenarios: state.bootstrap?.scenarios ?? [],
      focusTrace,
      signIn,
      setOperator,
      runScenario,
      refreshApprovals,
      resolveApproval,
    }),
    [state, focusTrace, signIn, setOperator, runScenario, refreshApprovals, resolveApproval],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): StoreValue {
  const value = useContext(StoreContext);
  if (!value) throw new Error('useStore must be used inside <StoreProvider>');
  return value;
}

/** Convenience accessor for the bootstrap payload once the console is ready. */
export function useEstate() {
  const { bootstrap } = useStore();
  if (!bootstrap) throw new Error('Estate accessed before bootstrap completed');
  return bootstrap;
}
